import { useState, useEffect } from 'react';
import { Menu, ChevronsLeft, ChevronsRight, ChevronDown, LogOut } from 'lucide-react';
import { cn } from '@repo/utils';
import { Button } from '../ui/Button';
import { ThemeToggle } from '../ui/ThemeToggle';
import { OrgUnitSwitcher } from '../../features/org/components/OrgUnitSwitcher';
import { ClientLogo } from '../branding/ClientLogo';
import logoUrl from '@repo/ui/assets/logo.svg';
import logoFullUrl from '@repo/ui/assets/logo-full.svg';
export interface NavSubItem {
 id: string;
 label: string;
 href: string;
 active?: boolean;
}

export interface NavItem {
 id: string;
 label: string;
 icon: React.ReactNode;
 href?: string;
 active?: boolean;
 subItems?: NavSubItem[];
}

interface AppShellProps {
  navItems: NavItem[];
  userName: string;
  userAvatar?: string | null;
  userEmail?: string;
  userRole: string;
  onNavigate: (href: string) => void;
  onLogout: () => void;
  children: React.ReactNode;
}

export function AppShell({
  navItems,
  userName,
  userAvatar,
  userEmail,
  userRole,
  onNavigate,
  onLogout,
  children,
}: AppShellProps) {
 const [collapsed, setCollapsed] = useState(false);
 const [userMenuOpen, setUserMenuOpen] = useState(false);
 const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHovered, setHeaderHovered] = useState(false);

  useEffect(() => {
    if (!userMenuOpen && !mobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (userMenuOpen) setUserMenuOpen(false);
        if (mobileMenuOpen) setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [userMenuOpen, mobileMenuOpen]);

 const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>(() => {
 const initial: Record<string, boolean> = {};
 navItems.forEach((item) => {
 if (item.subItems?.some((sub) => sub.active)) {
 initial[item.id] = true;
 }
 });
 return initial;
 });

  const showCollapsed = collapsed && !mobileMenuOpen;
  const toggleSubMenu = (id: string) => {
 setOpenSubMenus((prev) => ({
 ...prev,
 [id]: !prev[id],
 }));
 };

 const handleParentClick = (item: NavItem) => {
 if (item.subItems && item.subItems.length > 0) {
 if (showCollapsed) {
 setCollapsed(false);
 setOpenSubMenus((prev) => ({ ...prev, [item.id]: true }));
 } else {
 toggleSubMenu(item.id);
 }
 } else if (item.href) {
 onNavigate(item.href);
 setMobileMenuOpen(false);
 }
 };

 const mainNavItems = navItems.filter((item) => item.id !== 'configuration');
 const configItem = navItems.find((item) => item.id === 'configuration');

 const renderNavItem = (item: NavItem) => {
 const hasSubItems = item.subItems && item.subItems.length > 0;
 const isOpen = openSubMenus[item.id];
 const isParentActive = item.active || item.subItems?.some((sub) => sub.active);

  return (
    <div key={item.id} className="flex flex-col">
      <Button
        variant="ghost"
        onClick={() => handleParentClick(item)}
        aria-current={isParentActive ? 'page' : undefined}
        aria-expanded={hasSubItems ? isOpen : undefined}
        aria-haspopup={hasSubItems ? 'true' : undefined}
        className={cn(
          'w-full flex items-center transition-colors text-sm rounded-[6px]',
          showCollapsed ? 'justify-center py-2 px-0' : 'justify-between px-2.5 py-2',
          isParentActive && !hasSubItems
            ? cn(
                'bg-accent-50 text-text-primary border-accent-500 font-medium',
                showCollapsed ? 'border-l-2' : 'border-l-2 pl-2'
              )
            : isParentActive
              ? 'text-text-primary bg-surface-2 font-medium'
              : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
        )}
      >
        <div className={cn("flex items-center min-w-0", showCollapsed ? "justify-center" : "gap-3")}>
          <span className="flex-shrink-0 w-5 h-5 text-text-muted flex items-center justify-center" aria-hidden="true">{item.icon}</span>
          {!showCollapsed && <span className="truncate">{item.label}</span>}
        </div>
        {!showCollapsed && hasSubItems && (
          <ChevronDown
            className={cn(
              "w-4 h-4 text-text-muted transition-transform duration-200 flex-shrink-0",
              isOpen && "transform rotate-180"
            )}
          />
        )}
      </Button>

      {/* Sub items list */}
      {!showCollapsed && hasSubItems && isOpen && (
        <div className="pl-9 pr-2 mt-1 space-y-1 border-l border-border/60 ml-[18px]">
          {item.subItems?.map((sub) => (
            <Button
              variant="ghost"
              size="sm"
              key={sub.id}
              onClick={() => {
                onNavigate(sub.href);
                setMobileMenuOpen(false);
              }}
              aria-current={sub.active ? 'page' : undefined}
              className={cn(
                'w-full flex items-center justify-start text-left rounded-[6px]',
                sub.active
                  ? 'bg-accent-50 text-text-primary border-accent-500 font-medium border-l-2 pl-2'
                  : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary px-2.5'
              )}
            >
              <span className="truncate">{sub.label}</span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
  };

 const SidebarContent = (
 <>
  {/* Logo / company */}
  <div className={cn(
  "flex items-center h-16 border-b border-border flex-shrink-0 px-4",
  showCollapsed ? "justify-center" : "justify-between"
  )}>
  {showCollapsed ? (
  <Button
  variant="ghost"
  size="sm"
  onClick={() => setCollapsed(false)}
  onMouseEnter={() => setHeaderHovered(true)}
  onMouseLeave={() => setHeaderHovered(false)}
  className="w-8 h-8 p-0 rounded-[6px]"
  aria-label="Expand sidebar"
  >
  {headerHovered ? (
  <ChevronsRight className="w-5 h-5 text-text-primary" />
  ) : (
  <ClientLogo variant="icon" className="w-5 h-5 object-contain flex-shrink-0" />
  )}
  </Button>
  ) : (
  <>
  <div className="flex items-center min-w-0">
  <ClientLogo variant="full" className="h-7 w-auto max-w-[160px] object-contain flex-shrink-0" />
  </div>
  
  <Button
  variant="ghost"
  size="sm"
  onClick={() => setCollapsed(true)}
  className="hidden lg:flex items-center justify-center w-8 h-8 p-0 rounded-[6px] text-text-muted hover:text-text-primary flex-shrink-0"
  aria-label="Collapse sidebar"
  >
  <ChevronsLeft className="w-5 h-5" />
  </Button>

  {mobileMenuOpen && (
  <Button
  variant="ghost"
  size="sm"
  onClick={() => setMobileMenuOpen(false)}
  className="lg:hidden p-1 text-text-muted hover:text-text-primary"
  aria-label="Close menu"
  >
   <ChevronsLeft className="w-5 h-5" />
   </Button>
   )}
   </>
   )}
   </div>

  {/* Nav items */}
  <nav aria-label="Main navigation" className="flex-1 py-3 overflow-y-auto px-2 space-y-1">
    {mainNavItems.map(renderNavItem)}
  </nav>

 {/* Bottom section (Settings & Logout) */}
 <div className="border-t border-border flex-shrink-0 p-2 space-y-1 bg-surface-1">
 {configItem && renderNavItem(configItem)}
 
   <Button
   variant="ghost"
   onClick={() => {
   setMobileMenuOpen(false);
   onLogout();
   }}
   className={cn(
   'w-full flex items-center transition-colors text-sm rounded-[6px]',
   showCollapsed ? 'justify-center py-2 px-0' : 'justify-start px-2.5 py-2 gap-3',
   'text-text-secondary hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-950/20 dark:hover:text-danger-400'
   )}
   title="Sign Out"
   >
  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center" aria-hidden="true">
  <LogOut className="w-5 h-5 text-text-muted hover:text-inherit" />
  </span>
  {!showCollapsed && <span>Sign Out</span>}
   </Button>
  </div>

 {/* Sidebar Footer (Platform logo) */}
 <div className={cn(
 "border-t border-border flex-shrink-0 p-4 flex items-center",
 showCollapsed ? "justify-center" : "justify-start"
 )}>
 {showCollapsed ? (
 <img src={logoUrl} alt="Kraphene" className="w-6 h-6 object-contain flex-shrink-0" />
 ) : (
 <img src={logoFullUrl} alt="Kraphene" className="h-6 w-auto object-contain flex-shrink-0" />
 )}
 </div>
 </>
 );

 return (
 <div className="flex h-screen bg-bg overflow-hidden relative">
 <a
 href="#main-content"
 className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-toast bg-surface-1 text-accent-500 px-4 py-2 rounded-[6px] shadow-md border border-border"
 >
 Skip to content
 </a>

  {/* Mobile Sidebar Backdrop */}
  {mobileMenuOpen && (
    <div
      className="fixed inset-0 bg-black/50 z-overlay lg:hidden"
      onClick={() => setMobileMenuOpen(false)}
      role="presentation"
    />
  )}

  {/* Sidebar (Desktop + Mobile) */}
  <aside
    className={cn(
      'fixed inset-y-0 left-0 bg-surface-1 border-r border-border transition-[width,transform] flex flex-col',
      'lg:static lg:translate-x-0 lg:z-sidebar',
      mobileMenuOpen ? 'translate-x-0 w-64 shadow-xl z-modal' : '-translate-x-full z-sidebar lg:translate-x-0',
      collapsed ? 'lg:w-16' : 'lg:w-60',
    )}
    aria-label="Primary navigation"
    aria-modal={mobileMenuOpen ? true : undefined}
    role={mobileMenuOpen ? 'dialog' : undefined}
  >
    {SidebarContent}
  </aside>

 {/* Main area */}
 <div className="flex-1 flex flex-col min-w-0">
 {/* Top bar */}
 <header className="flex items-center justify-between h-16 px-4 lg:px-6 border-b border-border bg-surface-1 flex-shrink-0">
 <div className="flex items-center gap-3">
  <Button
  variant="ghost"
  size="sm"
  onClick={() => setMobileMenuOpen(true)}
  className="lg:hidden p-2 -ml-2 text-text-muted hover:text-text-primary"
  aria-label="Open menu"
  >
  <Menu className="w-5 h-5" />
  </Button>
 </div>

 {/* User menu and Theme toggle */}
 <div className="flex items-center gap-1 sm:gap-2">
 <OrgUnitSwitcher className="hidden sm:flex" />
 <ThemeToggle />

 <div className="relative">
  <Button
  variant="ghost"
  onClick={() => setUserMenuOpen((o) => !o)}
  className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-[6px] hover:bg-surface-2 transition-colors text-left"
  aria-haspopup="true"
  aria-expanded={userMenuOpen}
  aria-label="User menu"
  >
 {userAvatar ? (
 <img
 src={userAvatar}
 alt={userName}
 className="w-8 h-8 rounded-full object-cover flex-shrink-0"
 />
 ) : (
 <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center text-xs text-white font-semibold flex-shrink-0 uppercase">
 {userName ? userName.charAt(0) : 'U'}
 </div>
 )}
 <div className="hidden sm:flex flex-col min-w-0">
 <span className="text-sm font-semibold text-text-primary truncate max-w-[120px] leading-tight">{userName}</span>
  <span className="text-caption text-text-secondary uppercase tracking-wider font-semibold leading-tight mt-0.5">{userRole}</span>
  </div>
  </Button>

  {userMenuOpen && (
 <>
 <div 
 className="fixed inset-0 z-dropdown" 
 onClick={() => setUserMenuOpen(false)}
 aria-hidden="true"
 />
 <div className="absolute right-0 top-full mt-1.5 w-56 bg-surface-1 border border-border rounded-[10px] shadow-lg py-1.5 z-dropdown animate-in fade-in slide-in-from-top-1 duration-100">
 <div className="px-3.5 py-2.5 border-b border-border">
 <div className="flex items-center gap-2.5 mb-1.5">
 {userAvatar ? (
 <img
 src={userAvatar}
 alt={userName}
 className="w-8 h-8 rounded-full object-cover flex-shrink-0"
 />
 ) : (
 <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center text-xs text-white font-semibold flex-shrink-0 uppercase">
 {userName ? userName.charAt(0) : 'U'}
 </div>
 )}
 <div className="min-w-0">
    <p className="text-xs font-semibold text-text-primary truncate">{userName}</p>
    <p className="text-caption text-text-secondary truncate uppercase tracking-wide font-medium">{userRole}</p>
 </div>
 </div>
 {userEmail && (
 <p className="text-caption text-text-secondary truncate font-normal">{userEmail}</p>
 )}
 </div>

 {/* Mobile-only OrgUnitSwitcher */}
 <div className="sm:hidden px-3.5 py-2.5 border-b border-border bg-surface-2/30">
    <p className="text-caption text-text-secondary uppercase tracking-wider font-semibold mb-1.5">
      Branch / Unit
    </p>
 <OrgUnitSwitcher className="w-full max-w-none" />
 </div>
   <Button
   variant="ghost"
   size="sm"
   onClick={() => { setUserMenuOpen(false); onLogout(); }}
   className="w-full text-left px-3.5 py-2.5 text-xs font-medium text-text-secondary hover:bg-danger-50 hover:text-danger-600 dark:hover:bg-danger-950/20 dark:hover:text-danger-400 transition-colors flex items-center gap-2"
   >
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
  Sign Out
   </Button>
  </div>
 </>
 )}
 </div>
 </div>
 </header>

 {/* Page content */}
 <main id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 lg:px-8 lg:py-6">
 {children}
 </main>
 </div>
 </div>
 );
}
